import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDropList,
} from '@angular/cdk/drag-drop';

import { PipelineFlowNodeComponent } from '../pipeline-flow-node/pipeline-flow-node.component';
import { DomainCatalogDataService } from '@shared-lib/shared-services/domain-catalog-data.service';
import { ThemeService } from '@shared-lib/shared-services/theme.service';
import { NodeType, WorkflowConfig, WorkflowNode } from '../../shared-services/pipeline-workflow.model';
import { PipelineWorkflowService } from '../../shared-services/pipeline-workflow.service';

@Component({
  selector: 'app-pipeline-workflow-sidebar',
  standalone: true,
  imports: [CommonModule, CdkDropList, CdkDrag, CdkDragPlaceholder, CdkDragPreview, PipelineFlowNodeComponent],
  templateUrl: './pipeline-workflow-sidebar.component.html',
  styleUrl: './pipeline-workflow-sidebar.component.scss',
})
export class PipelineWorkflowSidebarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() set data(value: any) {
    this._data = value;
    this.syncFromData();
  }
  get data(): any {
    return this._data;
  }

  config: WorkflowConfig | null = null;
  activeNodeId: string | null = null;
  showAddModal = false;
  genericItems: any[] = [];
  themeMode: 'light' | 'dark' = 'light';
  sidebarCollapsed = false;
  private themeOverrides: Record<string, string> = {};
  private _data: any;

  private readonly sub = new Subscription();

  constructor(
    private readonly workflowService: PipelineWorkflowService,
    private readonly catalogs: DomainCatalogDataService,
    private readonly themeService: ThemeService,
  ) {}

  ngOnInit(): void {
    this.syncFromData();

    this.sub.add(this.workflowService.config$.subscribe((config) => {
      if (!this.isGenericMode) {
        this.config = config;
      }
    }));

    this.sub.add(this.workflowService.activeNodeId$.subscribe((activeNodeId) => {
      if (!this.isGenericMode) {
        this.activeNodeId = activeNodeId;
      }
    }));

    this.sub.add(this.themeService.theme$.subscribe((theme) => {
      this.themeMode = this.isDarkTheme(theme) ? 'dark' : 'light';
      this.themeOverrides = this.buildThemeOverrides(theme);
    }));

    this.sub.add(this.catalogs.vm$.subscribe((vm) => {
      if (!this.isDomainCatalogBinding) return;

      this.genericItems = vm.navItems
        .filter((item) => item.visible !== false)
        .map((item) => ({
          id: item.id,
          label: item.label,
          description: item.description,
          icon: item.icon,
          badgeLabel: item.badgeLabel || item.shortLabel,
          displayType: item.displayType,
          metaChips: item.metaChips,
          statusLabel: item.statusLabel,
          statusClass: item.statusClass,
          trailingAction: item.removable
            ? {
                visible: true,
                icon: 'remove',
                tooltip: 'Remove catalog',
                cssClass: 'catalog-remove-action',
                onClick: (catalogId: string) => this.catalogs.removeAddedCatalog(catalogId),
              }
            : null,
        }));
      this.activeNodeId = vm.activeCatalogId || this.genericItems[0]?.id || null;
      this.sidebarCollapsed = vm.sidebarCollapsed;
    }));
  }

  ngOnChanges(_: SimpleChanges): void {
    this.syncFromData();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get ui(): any {
    return this.data?.ui ?? {};
  }

  get flowNodeConfig(): any {
    return this.data?.flowNode ?? {};
  }

  get themeStyles(): Record<string, string> {
    const theme = this.catalogs.currentConfig?.theme;
    return {
      ...this.getConfigThemeStyles(theme),
      ...this.themeOverrides,
    };
  }

  private getConfigThemeStyles(theme: any): Record<string, string> {
    const styles = { ...(theme?.[this.themeMode] || {}) };
    delete styles['--primary'];
    delete styles['--primary-end'];
    delete styles['--primary-rgb'];
    delete styles['--primary-bg'];
    return styles;
  }

  private buildThemeOverrides(theme: any): Record<string, string> {
    if (!theme) return {};
    const primary = theme.button?.primaryBg || theme.layout?.sidebar?.bg || '#043D63';
    const primaryEnd = theme.button?.primaryHoverBg || primary;
    const primaryRgb = this.hexToRgb(primary) || '4, 61, 99';

    return {
      '--primary': primary,
      '--primary-end': primaryEnd,
      '--primary-hover': primaryEnd,
      '--primary-rgb': primaryRgb,
      '--primary-bg': `rgba(${primaryRgb}, ${this.themeMode === 'dark' ? '0.16' : '0.08'})`,
      '--bg-app': theme.layout?.content?.bg || 'var(--dvm-content-bg, #d9dbdd)',
      '--text-main': theme.layout?.content?.text || 'var(--dvm-content-text, #202124)',
      '--text-muted': theme.chart?.axisLabelColor || 'var(--dvm-form-placeholder-color, #5f6368)',
    };
  }

  private hexToRgb(hex: string): string | null {
    const normalized = hex.replace('#', '').trim();
    if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  }

  private isDarkTheme(theme: any): boolean {
    const bg = String(theme?.layout?.content?.bg || '').replace('#', '').slice(0, 6);
    if (!/^[0-9a-f]{6}$/i.test(bg)) return this.themeService.currentThemeName === 'dark';
    const r = parseInt(bg.slice(0, 2), 16);
    const g = parseInt(bg.slice(2, 4), 16);
    const b = parseInt(bg.slice(4, 6), 16);
    return r * 0.299 + g * 0.587 + b * 0.114 < 128;
  }

  get isGenericMode(): boolean {
    return Array.isArray(this.data?.items) || this.isDomainCatalogBinding;
  }

  get isDomainCatalogBinding(): boolean {
    return this.data?.binding?.source === 'domain-catalogs';
  }

  get renderedNodes(): any[] {
    return this.isGenericMode ? this.genericItems : (this.config?.nodes || []);
  }

  get enableDrag(): boolean {
    return this.readFlag(this.ui?.enableDrag, !this.isGenericMode);
  }

  get useCustomAddAction(): boolean {
    return typeof this.data?.onAddAction === 'function' || this.isDomainCatalogBinding;
  }

  get enableHoverSelection(): boolean {
    return this.readFlag(this.ui?.hoverSelection, !this.isGenericMode);
  }

  get nodeTypeOptions(): Array<{ type: NodeType; title: string; description: string }> {
    const configured = this.ui?.addNodeModal?.nodeTypes;
    if (Array.isArray(configured) && configured.length) {
      return configured;
    }

    return [
      { type: 'AUTO_APPROVE', title: 'Auto-approve', description: 'Automatically route without human review' },
      { type: 'PARALLEL', title: 'Parallel gate', description: 'Multiple approvers review simultaneously' },
      { type: 'HIERARCHY', title: 'Hierarchy chain', description: 'Sequential approval up the management chain' },
    ];
  }

  selectNode(nodeId: string): void {
    if (this.isDomainCatalogBinding) {
      this.activeNodeId = nodeId;
      this.catalogs.selectCatalog(nodeId);
      return;
    }

    if (Array.isArray(this.data?.items)) {
      this.activeNodeId = nodeId;
      this.data?.onSelectItem?.(nodeId);
      return;
    }

    this.workflowService.selectNode(nodeId);
  }

  dropNode(event: CdkDragDrop<WorkflowNode[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    if (this.isGenericMode) {
      this.data?.onReorderItems?.(event.previousIndex, event.currentIndex);
      return;
    }
    this.workflowService.reorderNodes(event.previousIndex, event.currentIndex);
  }

  addNewNode(type: NodeType): void {
    if (this.isGenericMode) {
      this.data?.onAddAction?.(type);
      this.showAddModal = false;
      return;
    }
    this.workflowService.addNode(type);
    this.showAddModal = false;
  }

  buildFlowNodeData(node: any, index: number): any {
    const normalizedNode = this.isGenericMode ? this.createGenericNode(node, index) : node;
    return {
      node: normalizedNode,
      index,
      label: this.isGenericMode ? String(node?.label || node?.title || '') : undefined,
      description: this.isGenericMode ? String(node?.description || '') : undefined,
      icon: this.isGenericMode ? String(node?.icon || '') : undefined,
      badgeLabel: this.isGenericMode ? String(node?.badgeLabel || node?.shortLabel || '') : undefined,
      displayType: this.isGenericMode ? this.normalizeNodeType(node?.displayType || node?.nodeType) : undefined,
      metaChips: this.isGenericMode ? this.normalizeChips(node?.metaChips, node?.meta) : undefined,
      statusLabel: this.isGenericMode ? String(node?.statusLabel || '') : undefined,
      statusClass: this.isGenericMode ? String(node?.statusClass || '') : undefined,
      trailingAction: this.isGenericMode ? this.normalizeTrailingAction(node) : undefined,
      config: this.flowNodeConfig,
      isActive: normalizedNode.id === this.activeNodeId,
      onNodeClick: (nodeId: string) => this.selectNode(nodeId),
      onInsertBefore: () => {
        this.triggerAddAction();
      },
    };
  }

  triggerAddAction(): void {
    if (this.isDomainCatalogBinding) {
      this.catalogs.openAddCatalogModal();
      return;
    }

    if (typeof this.data?.onAddAction === 'function') {
      this.data.onAddAction();
      return;
    }

    this.showAddModal = true;
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.showAddModal = false;
    }
  }

  readFlag(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  readText(value: unknown, fallback: string): string {
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
  }

  private syncFromData(): void {
    if (this.isDomainCatalogBinding) {
      this.catalogs.load(
        this.data?.configUrl || 'assets/dashboard-config/domain-catalogs.json',
        this.data?.dataApiUrl,
      );
      this.genericItems = [];
      this.activeNodeId = null;
      this.config = null;
      return;
    }

    if (Array.isArray(this.data?.items)) {
      this.genericItems = this.data.items;
      this.activeNodeId = this.readText(this.data?.activeId, this.genericItems[0]?.id || '') || null;
      this.config = null;
      return;
    }

    if (this.data?.workflow) {
      this.workflowService.loadConfig(this.data.workflow);
    }
  }

  private createGenericNode(item: any, index: number): WorkflowNode {
    const id = String(item?.id || `generic-node-${index + 1}`);
    const type = this.normalizeNodeType(item?.displayType || item?.nodeType);
    return {
      id,
      type,
      label: String(item?.label || item?.title || id),
      isValid: item?.statusClass !== 'chip-warning' && item?.statusClass !== 'chip-incomplete',
      hasWarning: item?.statusClass === 'chip-warning',
      triggerConditions: [],
      approvers: [],
      decisionRule: { type: 'ANY_ONE', description: '' },
      delegationUsers: [],
      sla: {
        reminderHours: 0,
        maxEscalations: 0,
        onTimeout: 'ESCALATE',
        escalationChain: [],
      },
    };
  }

  private normalizeNodeType(value: unknown): NodeType {
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized === 'PARALLEL' || normalized === 'HIERARCHY') {
      return normalized;
    }
    return 'AUTO_APPROVE';
  }

  private normalizeChips(value: unknown, fallback?: unknown): string[] {
    const raw = Array.isArray(value) ? value : Array.isArray(fallback) ? fallback : fallback ? [fallback] : [];
    return raw.map((item) => String(item || '').trim()).filter(Boolean);
  }

  private normalizeTrailingAction(node: any): any {
    const action = node?.trailingAction;
    if (!action) {
      return null;
    }

    return {
      visible: this.readFlag(action.visible, true),
      icon: this.readText(action.icon, 'remove'),
      tooltip: this.readText(action.tooltip, 'Remove'),
      cssClass: this.readText(action.cssClass, ''),
      onClick: typeof action.onClick === 'function' ? action.onClick : undefined,
    };
  }
}

